import React, { useEffect, useState } from "react";
import { NavLink, withRouter } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import StratusLogo from "../../../images/v2/stratus_logo.png";
import Building from "../assets/svg/icons/common/Building";
import CaretDown from "../assets/svg/icons/common/CaretDown";
import CaretLeft from "../assets/svg/icons/common/CaretLeft";
import Chargeback from "../assets/svg/icons/common/Chargeback";
import CloseIcon from "../assets/svg/icons/common/CloseIcon";
import PlusIcon from "../assets/svg/icons/common/Plus";
import Files from "../assets/svg/icons/common/Files";
import Home from "../assets/svg/icons/common/Home";
import JoyStick from "../assets/svg/icons/common/JoyStick";
import Money from "../assets/svg/icons/common/Money";
import PCLogoFull from "../assets/svg/icons/common/PCLogoFull";
import PCLogoIcon from "../assets/svg/icons/common/PCLogoIcon";
import Store from "../assets/svg/icons/common/Store";
import SuitcaseIcon from "../assets/svg/icons/common/SuitcaseIcon";
import DropDown from "../components/DropDown/DropDown";
import useCommonStore from "../stores/common";
import useDomainStore from "../stores/domain";
import useUserStore, { userProfileDropdownOptions } from "../stores/user";
import { useOrganizationLogo } from "../hooks/general/useOrganizationLogo";
import style from "./SideNav.module.scss";
import Avatar from "../components/Avatar/Avatar";
import ButtonIcon from "../components/ButtonIcon/ButtonIcon";
import config from "../../config";
import { isEntitySuperAdmin } from "../../components/helper/helper";
const SideNav = ({ location, history }) => {
  const loggedInUser = useUserStore((state) => state.user);
  const isV2Enabled = useUserStore((state) => state.isV2Enabled);
  const basePath = isV2Enabled ? "" : "/v2";
  const isViewResiduals = useUserStore((state) => state.isViewResiduals);
  const isAgent = useUserStore((state) => state.isAgent);
  const isPCAdmin = useUserStore((state) => state.isPCAdmin);
  const isAdmin = useUserStore((state) => state.isAdmin);
  const user = useUserStore((state) => state.user);
  const isDashboardAllowedOrgAdmin = useUserStore(
    (state) => state.isDashboardAllowedOrgAdmin,
  );
  const canViewResidualData = useUserStore(
    (state) => state.canViewResidualData,
  );
  const isKurvEntityAdmin = loggedInUser?.role == "admin" && loggedInUser?.organization?.ems_entity == 'kurvpay';
  const isHouseRiskAdmin = loggedInUser?.role == "admin" && loggedInUser?.organization?.id == config.paymentcloud.HOUSE_RISK_ORG_ID;
  const sideNavCollapsed = useCommonStore((state) => state.sideNavCollapsed);
  const mobileMenuOpen = useCommonStore((state) => state.mobileMenuOpen);
  const toggleMobileMenu = useCommonStore((state) => state.toggleMobileMenu);
  const setSideNavCollapsed = useCommonStore(
    (state) => state.setSideNavCollapsed,
  );
  const [expandedMenus, setExpandedMenus] = useState({});
  const [orgLogoFull, setOrgLogoFull] = useState(null);
  const [orgLogoIcon, setOrgLogoIcon] = useState(null);

  // Domain store
  const { isKurvDomain, initializeDomain } = useDomainStore();

  // Initialize domain detection
  useEffect(() => {
    initializeDomain();
  }, [initializeDomain]);

  useEffect(() => {
    if (isKurvDomain) {
      setOrgLogoFull("/images/kurv/kurv-logo.svg");
      setOrgLogoIcon("/images/kurv/kurv-favicon.svg");
    } else if (user?.organization?.whitelabel_slug) {
      const slug = user.organization.whitelabel_slug;
      setOrgLogoFull(`/img/${slug}/logo-solid.png`);
      setOrgLogoIcon(`/img/${slug}/logo-solid.png`);
    }
  }, [user, isKurvDomain]);

  const setNavCollapsing = (value) => {
    if (value) {
      setSideNavCollapsed(true);
      setExpandedMenus({});
    } else {
      setSideNavCollapsed(false);
    }
  };

  const toggleMenu = (menu) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const isExactMatch = (path) => {
    return location.pathname === path;
  };

  const handleLinkClick = () => {
    if (window.innerWidth < 1280 && mobileMenuOpen) {
      toggleMobileMenu();
    }
  };

  const getParentLinkClassName = (link) => {
    const path = `${basePath}/${link.route}`;
    const hasActiveChild =
      link.hasChild &&
      link.childLinks.some((childLink) => {
        const childPath = `${basePath}/${childLink.route}`;
        return (
          location.pathname === childPath ||
          location.pathname.startsWith(`${childPath}`)
        );
      });

    // Check for nested routes only for Organizations menu
    const isOrganizationsNestedRoute =
      link.route === "organizations" &&
      location.pathname.includes("/organizations");

    const className =
      isExactMatch(path) || hasActiveChild || isOrganizationsNestedRoute
        ? style.active_item
        : "";

    return className;
  };

  const getChildLinkClassName = (childLink) => {
    const childPath = `${basePath}/${childLink.route}`;
    const isStartsWithMatch = location.pathname.startsWith(`${childPath}`);

    return isExactMatch(childPath) || isStartsWithMatch
      ? style.child_active_item
      : "";
  };

  const links = [
    {
      name: "Overview",
      route: "dashboard",
      icon: <Home />,
      show: isEntitySuperAdmin(user) || isDashboardAllowedOrgAdmin,
    },
    {
      name: "Applications",
      route: "applications",
      icon: <Files />,
      show: true,
    },
    {
      name: "Organizations",
      route: "organizations",
      icon: <Building />,
      show: (isAgent && isViewResiduals) || isAdmin || isPCAdmin || isHouseRiskAdmin,
    },
    {
      name: "Merchants",
      route: "merchants",
      icon: <Store />,
      show: true,
    },
    {
      name: "Chargeback",
      route: "chargeback",
      icon: <Chargeback />,
      show: isEntitySuperAdmin(user) || isDashboardAllowedOrgAdmin,
    },
    {
      name: "Residuals",
      route: "residuals",
      icon: <Money />,
      // canViewResidualData alone, matching ResidualRoute and the server's
      // isConfiguredForResidualData(): the toggle for everyone, plus a split
      // and a rep ID for an agent. isDashboardAllowedOrgAdmin is deliberately
      // not ANDed in — it keys off the org's replica_org_id, which no residual
      // endpoint consults, so it hid the link from agents the API would serve.
      show: canViewResidualData,
    },
    {
      name: "System",
      route: "transactions",
      icon: <JoyStick />,
      hasChild: true,
      show: isEntitySuperAdmin(user),
      childLinks: [
        {
          name: "Equipment",
          route: "equipments",
          show: isPCAdmin,
        },
        {
          name: "Equipment (v1)",
          route: "old-equipments",
          show: isPCAdmin,
        },
        {
          name: "MCC Codes",
          route: "mcc",
          show: isPCAdmin,
        },
        {
          name: "Processors",
          route: "processors",
          show: isPCAdmin,
        },
        {
          name: "Kurv Offices",
          route: "offices",
          show: isEntitySuperAdmin(user),
        },
      ],
    },
    {
      name: "Tools",
      route: "",
      icon: <SuitcaseIcon />,
      hasChild: true,
      show: true,
      childLinks: [
        {
          name: "Templates",
          route: "templates",
          show: true,
        },
        {
          name: "Service Tickets",
          route: "service-tickets",
          show: true,
        },
        {
          name: "Manager Approval",
          route: "manager-approval",
          show: isEntitySuperAdmin(user),
        },
        {
          name: "Shipping",
          route: "shipping",
          show: isEntitySuperAdmin(user) || isDashboardAllowedOrgAdmin,
        },
        {
          name: "Announcements",
          route: "announcement",
          show: isPCAdmin,
        },
      ],
    },
  ];

  return (
    <>
      <nav
        className={`${style.side_nav} ${sideNavCollapsed ? style.collapsed : ""} ${isKurvDomain ? style.kurv_theme : ""} ${mobileMenuOpen ? style.mobileMenuOpen : ""}`}
      >
        <div className={style.side_nav_header}>
          <div className={style.sideNavHeaderLogo}>
            {!sideNavCollapsed ? (
              <span>
                {orgLogoFull ? (
                  <img
                    src={orgLogoFull}
                    alt="Logo"
                    className={style.org_logo_full}
                  />
                ) : (
                  <PCLogoFull />
                )}
              </span>
            ) : (
              <span>
                {orgLogoIcon ? (
                  <img
                    src={orgLogoIcon}
                    alt="Logo"
                    className={style.org_logo_icon}
                  />
                ) : (
                  <PCLogoIcon />
                )}
              </span>
            )}
          </div>

          <span
            className={style.sideNavHeaderLogoCloseIcon}
            onClick={() => toggleMobileMenu()}
          >
            <CloseIcon />
          </span>

          <button
            className={`${style.collapseIcon} ${sideNavCollapsed ? style.collapsed : ""}`}
            onClick={() => setNavCollapsing(!sideNavCollapsed)}
          >
            <CaretLeft />
          </button>
        </div>
        <div className={style.createButtonContainer}>
          <ButtonIcon
            size="large"
            width="100%"
            bgColor={isKurvDomain ? "#22A276" : "#4C70EF"}
            textColor="#FFF"
            onClick={() => {
              toggleMobileMenu();
              history.push("/create-app/usa");
            }}
            className={style.createButton}
          >
            <PlusIcon />
            Create
          </ButtonIcon>
        </div>
        <ul
          className={`${style.nav_list} ${sideNavCollapsed ? style.collapsed : ""}`}
        >
          {links
            .filter((link) => link?.show ?? true)
            .map(
              (link, index) =>
                link.show && (
                  <li
                    key={index}
                    className={getParentLinkClassName(link)}
                    data-for={link.route}
                    data-tip
                  >
                    {!link.hasChild && (
                      <ReactTooltip
                        id={link.route}
                        aria-haspopup="true"
                        place="right"
                        type="dark"
                        effect="solid"
                      >
                        <p>{link.name}</p>
                      </ReactTooltip>
                    )}
                    <NavLink
                      to={link.hasChild ? "#" : `${basePath}/${link.route}`}
                      onClick={() => {
                        if (link.hasChild) {
                          toggleMenu(link.name);
                        } else {
                          handleLinkClick();
                        }
                      }}
                    >
                      <span className="icon">{link.icon}</span>
                      <span className={`${style.nav_text} nav-text`}>
                        {link.name}
                      </span>
                      {link.hasChild && !sideNavCollapsed && (
                        <span>
                          <CaretDown />
                        </span>
                      )}
                    </NavLink>

                    {/* child links */}
                    {link.hasChild && expandedMenus[link.name] && (
                      <ul className={style.child_links}>
                        {link.childLinks
                          .filter((childLink) => childLink?.show ?? true)
                          .map((childLink, index) => (
                            <li
                              key={index}
                              className={getChildLinkClassName(childLink)}
                              data-for={childLink.route}
                              data-tip
                            >
                              <NavLink
                                to={`${basePath}/${childLink.route}`}
                                onClick={handleLinkClick}
                              >
                                <span className={style.child_link_icon}>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="6"
                                    height="6"
                                    viewBox="0 0 6 6"
                                    fill="none"
                                  >
                                    <circle cx="3" cy="3" r="3" fill="white" />
                                  </svg>
                                </span>
                                <span
                                  className={`${style.child_link_text} nav-text`}
                                >
                                  {childLink.name}
                                </span>
                              </NavLink>
                              <ReactTooltip
                                id={childLink.route}
                                aria-haspopup="true"
                                place="right"
                                type="dark"
                                effect="solid"
                              >
                                <p>{childLink.name}</p>
                              </ReactTooltip>
                            </li>
                          ))}
                      </ul>
                    )}
                  </li>
                ),
            )}
        </ul>
        <div className={style.side_nav_footer}>
          <div className={style.mobileUserProfileArea}>
            <NavLink to="/v2/help">
              <span className="icon">
                {" "}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12.0002 2.25C10.3169 2.24963 8.66213 2.68508 7.19704 3.51396C5.73194 4.34285 4.50641 5.53692 3.63971 6.97997C2.77302 8.42301 2.29469 10.0659 2.25129 11.7486C2.20789 13.4314 2.60089 15.0967 3.39205 16.5825L2.32799 19.7747C2.23985 20.039 2.22706 20.3226 2.29105 20.5938C2.35504 20.8649 2.49328 21.1129 2.69029 21.3099C2.88729 21.5069 3.13526 21.6451 3.40642 21.7091C3.67757 21.7731 3.96119 21.7603 4.22549 21.6722L7.41768 20.6081C8.72527 21.3036 10.1741 21.6921 11.6543 21.744C13.1344 21.7959 14.6069 21.51 15.9601 20.9079C17.3132 20.3057 18.5114 19.4033 19.4636 18.269C20.4159 17.1346 21.0972 15.7983 21.4559 14.3613C21.8146 12.9244 21.8412 11.4246 21.5337 9.97578C21.2263 8.52701 20.5928 7.16732 19.6813 5.99992C18.7699 4.83253 17.6045 3.88811 16.2736 3.23836C14.9427 2.58861 13.4812 2.25061 12.0002 2.25ZM12.0002 20.25C10.5498 20.251 9.12495 19.8691 7.86955 19.1428C7.77763 19.0895 7.67544 19.0563 7.56974 19.0455C7.46404 19.0347 7.35724 19.0464 7.25643 19.08L3.75018 20.25L4.91924 16.7438C4.95294 16.643 4.96485 16.5362 4.95418 16.4305C4.94351 16.3248 4.91051 16.2226 4.85736 16.1306C3.94796 14.5584 3.58284 12.7299 3.81864 10.929C4.05444 9.12801 4.87798 7.4552 6.16151 6.17006C7.44503 4.88491 9.11679 4.05925 10.9175 3.82118C12.7181 3.5831 14.547 3.94591 16.1204 4.85333C17.6938 5.76074 18.9238 7.16204 19.6196 8.83983C20.3153 10.5176 20.4379 12.3781 19.9684 14.1327C19.4989 15.8873 18.4634 17.4379 17.0227 18.5439C15.582 19.65 13.8165 20.2497 12.0002 20.25Z"
                    fill="#3A383F"
                  />
                </svg>
              </span>
              <span className={`${style.nav_text} nav-text`}>Help</span>
            </NavLink>

            <div className={style.userProfileDropDown}>
              <DropDown options={userProfileDropdownOptions(history)}>
                <div className={style.user_section}>
                  <Avatar size="medium" />
                  <div className={style.user_info}>
                    <span>{loggedInUser?.name}</span>
                    <span>{loggedInUser?.email}</span>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M13.3538 6.35375L8.35378 11.3538C8.30735 11.4002 8.2522 11.4371 8.1915 11.4623C8.13081 11.4874 8.06574 11.5004 8.00003 11.5004C7.93433 11.5004 7.86926 11.4874 7.80856 11.4623C7.74786 11.4371 7.69272 11.4002 7.64628 11.3538L2.64628 6.35375C2.55246 6.25993 2.49976 6.13269 2.49976 6C2.49976 5.86732 2.55246 5.74007 2.64628 5.64625C2.7401 5.55243 2.86735 5.49973 3.00003 5.49973C3.13272 5.49973 3.25996 5.55243 3.35378 5.64625L8.00003 10.2931L12.6463 5.64625C12.6927 5.5998 12.7479 5.56295 12.8086 5.53781C12.8693 5.51267 12.9343 5.49973 13 5.49973C13.0657 5.49973 13.1308 5.51267 13.1915 5.53781C13.2522 5.56295 13.3073 5.5998 13.3538 5.64625C13.4002 5.69271 13.4371 5.74786 13.4622 5.80856C13.4874 5.86925 13.5003 5.93431 13.5003 6C13.5003 6.0657 13.4874 6.13076 13.4622 6.19145C13.4371 6.25215 13.4002 6.3073 13.3538 6.35375Z"
                      fill="#A4A3A8"
                    />
                  </svg>
                </div>
              </DropDown>
            </div>
          </div>

          <div className={style.sideNavPoweredBy}>
            <span>Powered by</span>
            <img src={StratusLogo} alt="Stratus Logo" />
          </div>
        </div>
      </nav>
      <div
        className={`${style.mobileMenuBackdrop} ${mobileMenuOpen ? style.mobileMenuBackdropOpen : ""}`}
        onClick={() => toggleMobileMenu()}
      ></div>
    </>
  );
};

export default withRouter(SideNav);
